import { Box, CircularProgress, makeStyles, Typography } from "@material-ui/core";
import * as React from "react";
import { useHistory, useLocation } from "react-router";

import { Agent } from "@/api/generated";
import { CompatibilityAgentCard } from "@/components/agent/CompatibilityAgentCard";
import { CustomButton } from "@/components/Button";
import { FeaturedPlannerCard, PlannerListCard } from "@/components/planner_onboarding/PlannerCards";
import { usePlannerReducedMotion } from "@/components/planner_onboarding/PlannerReveal";
import { RecommendedPlannerCarousel } from "@/components/planner_onboarding/RecommendedPlannerCarousel";
import { useDevice } from "@/hooks/useDevice";
import { useMasterAgentSearchTagMap } from "@/hooks/useGetMasterData";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { hasUsableAgentId } from "@/utilities/agentId";
import { NavigationPath } from "@/utilities/enums";
import { AGENT_DETAIL_URL_SOURCE, AgentDetailUrlSource } from "@/utilities/userOffer";

import { SearchModal } from "./components/AgentSearchModal";
import { useAgentSearchList } from "./hooks/useAgentSearchList";
import { resolveAgentListViewState } from "./utils/agentListViewState";
import { resolveLoadMoreViewState } from "./utils/loadMoreViewState";
import { buildSearchConditionLabel } from "./utils/searchConditionLabel";

const RESULT_DESCRIPTION_WITHOUT_CONDITIONS = "登録情報をもとに、相性スコアの高い順にプランナーをご紹介しています。";
const RESULT_DESCRIPTION_WITH_CONDITIONS = "選択した条件に合うプランナーを、相性スコアの高い順にご紹介しています。";
const EMPTY_MESSAGE_WITH_CONDITIONS = "条件に一致するプランナーが見つかりませんでした。";
const EMPTY_MESSAGE_WITHOUT_CONDITIONS = "ご紹介できるプランナーが見つかりませんでした。";
const LOAD_MORE_ERROR_MESSAGE = "続きの読み込みに失敗しました。";
// 条件なしの一覧は登録導線（診断結果）と同じ見せ方にする（Trello #234、Figma「施策_プランナー一覧」）。
// 相性順の上位3人を大判カード、4人目以降を一覧カードで出す。条件で絞り込んだ一覧は従来のカードのまま
const FEATURED_PLANNER_COUNT = 3;
const OTHER_PLANNERS_HEADING = "ほかの認定プランナーも見る";
const OTHER_PLANNERS_DESCRIPTION = "相性スコアの高い順に表示しています。";
const isAgentDetailSourceValue = (value: string | null | undefined): value is AgentDetailUrlSource =>
  !!value && (Object.values(AGENT_DETAIL_URL_SOURCE) as string[]).includes(value);

const useStyles = makeStyles(() => ({
  content: { paddingBottom: 32, [theme.breakpoints.up("md")]: { paddingTop: 32 } },
  pageTitle: { fontSize: 24, fontWeight: 700, lineHeight: 1.3, marginBottom: 24 },
  columns: {
    display: "grid",
    gridTemplateColumns: "384px minmax(0,1fr)",
    gap: 40,
    [theme.breakpoints.between("md", "md")]: { gridTemplateColumns: "300px minmax(0,1fr)", gap: 24 },
  },
  side: { position: "sticky", top: 88, alignSelf: "start" },
  results: {
    minWidth: 0,
    padding: "24px 24px 0",
    display: "flex",
    flexDirection: "column",
    gap: 24,
    [theme.breakpoints.up("md")]: { padding: 0 },
  },
  heading: { display: "flex", flexDirection: "column", gap: 8 },
  titleRow: { display: "flex", gap: 12, alignItems: "center" },
  resultTitle: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.5,
    [theme.breakpoints.up("md")]: { color: theme.palette.semantic["text/heading"] },
  },
  count: { fontSize: 14, color: theme.palette.text.secondary },
  plannerLabel: { whiteSpace: "nowrap" },
  description: { fontSize: 14, lineHeight: 1.5, color: theme.palette.semantic["text/primary"] },
  list: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr)",
    gap: 12,
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 24 },
    [theme.breakpoints.between("md", "md")]: { gridTemplateColumns: "minmax(0,1fr)" },
  },
  // SP のカルーセルは結果ブロックの左右 24px を打ち消して画面端まで使い、次のカードの端を見せる（登録導線と同じ full-bleed）
  carouselBleed: { margin: "0 -24px" },
  // PC は横スクロールにせず上位3人を横に並べる（Figma は SP のみ）。サイドバーが狭まる md だけは一覧グリッドと同じく1列に積む
  featuredGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: 24,
    [theme.breakpoints.between("md", "md")]: { gridTemplateColumns: "minmax(0,1fr)" },
    // 3列ではカード幅が約250pxになり CTA「このプランナーの詳細を見る」が2行に折り返すため、グリッド内だけ1行に収める
    // （CustomButton のサイズ指定は &&& で効いているので、同じ強さで上書きする）
    [theme.breakpoints.up("lg")]: {
      "&&& article button": { fontSize: 14, paddingLeft: 8, paddingRight: 8, whiteSpace: "nowrap" },
    },
  },
  otherPlanners: { display: "flex", flexDirection: "column", gap: 16 },
  otherHeading: { fontSize: 16, fontWeight: 700, lineHeight: 1.5, color: theme.palette.semantic["text/heading"] },
  otherDescription: { marginTop: 8, fontSize: 14, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"] },
  conditionBar: {
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: "12px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  conditionActions: { display: "flex", gap: 12, "& > *": { flex: 1, minWidth: 0 } },
  conditionLabel: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary, overflowWrap: "anywhere" },
  state: {
    backgroundColor: theme.palette.semantic["surface/card"],
    borderRadius: 16,
    padding: "40px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 16,
    "& button": { width: "100%", [theme.breakpoints.up("md")]: { width: 240 } },
  },
  stateTitle: { fontSize: 16, fontWeight: 700, lineHeight: 1.3 },
  more: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    "& button": { width: "100%", [theme.breakpoints.up("md")]: { width: 240 } },
  },
  skeleton: {
    borderRadius: 16,
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: 24,
    minHeight: 264,
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  skeletonProfile: { display: "flex", alignItems: "center", gap: 12 },
  skeletonAvatar: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.palette.semantic["field/bg"],
  },
  skeletonHead: { display: "flex", flexDirection: "column", gap: 8, flex: 1 },
  skeletonLine: { height: 16, width: "100%", borderRadius: 4, backgroundColor: theme.palette.semantic["field/bg"] },
}));

export const AgentSearchPage: React.FC = () => {
  const { isMobile } = useDevice();
  // カルーセルの移動を「動きを減らす」設定に合わせる（登場の演出は登録導線だけのもので、一覧では使わない）
  const reduced = usePlannerReducedMotion();
  // マスタタグの取得はこの1インスタンスに集約し、モーダルへは props で渡す。
  // モーダル側でも同じフックを呼ぶと state がインスタンスごとに分かれ、
  // モーダルの再試行で取得できても条件バーのラベル用辞書が空のままになるため
  const {
    tagIdToNameMap,
    tags: masterTags,
    loading: isMasterTagsLoading,
    refetch: refetchMasterTags,
  } = useMasterAgentSearchTagMap();

  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const rawTags = params.get("tags");
  const tagIdsFromUrl = React.useMemo(() => {
    if (!rawTags) return [];
    return (
      rawTags
        .split(",")
        .map(Number)
        // tags= や tags=, のような「空」クエリが入った場合に 0 が混ざり、
        // 「検索済み扱い」になって詳細遷移が search_result になるのを防ぐ
        .filter((n) => Number.isFinite(n) && n > 0)
    );
  }, [rawTags]);
  const originSource = React.useMemo<AgentDetailUrlSource | null>(() => {
    const searchParams = new URLSearchParams(location.search);
    const querySource = searchParams.get("source");
    if (isAgentDetailSourceValue(querySource)) {
      return querySource;
    }
    if (typeof window !== "undefined") {
      const storedSource = window.sessionStorage.getItem("agent_search_source");
      if (isAgentDetailSourceValue(storedSource)) {
        return storedSource;
      }
    }
    return null;
  }, [location.search]);
  const isFromLearningArticle = originSource === AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE;

  React.useEffect(() => {
    if (!isFromLearningArticle) return;
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get("source") === AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE) return;
    currentParams.set("source", AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE);
    history.replace({
      pathname: location.pathname,
      search: `?${currentParams.toString()}`,
    });
  }, [isFromLearningArticle, history, location.pathname, location.search]);

  React.useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem("agent_search_source");
      }
    };
  }, []);

  // 一覧を先に見せる導線のため、条件選択モーダルは閉じた状態で着地する
  const [isModalOpen, setModalOpen] = React.useState(false);

  const {
    agents,
    isLoading,
    isLoadingMore,
    error,
    loadMoreError,
    lastLoadAddedNothing,
    hasMore,
    isAutoLoadSupported,
    total,
    fetchAgents,
    fetchMore,
  } = useAgentSearchList();

  // 検索条件バーに出す条件文（条件未選択・マスタに無いIDだけのときは空文字）
  const conditionLabel = buildSearchConditionLabel(tagIdsFromUrl, tagIdToNameMap);
  // 「条件あり」の判定はURLを正とする。ラベル（マスタタグ辞書に名前を引けたか）を根拠にすると、
  // マスタ取得に失敗したときに絞り込み済みなのにリセット導線が消え、絞り込みから抜け出せなくなる。
  // 計測側の hasSearched も tagIdsFromUrl 由来のため、表示と計測の判定基準も揃う。
  // ラベルが空のときは、ラベルの表示だけを省く（リセット導線・バー高さ・説明文はURL基準のまま）。
  const hasConditions = tagIdsFromUrl.length > 0;
  const classes = useStyles();

  // 取得の起点はURLの tags 一本にする。検索・リセットはURLを書き換えるだけにして、
  // 初回ロードと操作で取得が二重に走らないようにする
  React.useEffect(() => {
    fetchAgents(tagIdsFromUrl);
  }, [fetchAgents, tagIdsFromUrl]);

  // 一覧末尾の番兵を監視して、表示領域に入ったら次ページを足す。
  // 監視対象が入れ替わるたびに前の監視を切らないと、外れた要素の observer が残って多重発火・リークになる
  const loadMoreObserverRef = React.useRef<IntersectionObserver>();
  const loadMoreSentinelRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreObserverRef.current?.disconnect();
      // 番兵が消えるとき（hasMore が false になった・一覧が消えた）は監視を張り直さない
      if (!node) return;
      loadMoreObserverRef.current = new IntersectionObserver(
        (entries) => {
          // 連打・多重発火は fetchMore 側の ref ガードで弾かれるため、ここでは発火条件だけを見る
          if (entries.some((entry) => entry.isIntersecting)) void fetchMore();
        },
        // 末尾に着いてから取り始めると待ち時間が見えるため、少し手前で取得を始める
        { rootMargin: "200px" }
      );
      loadMoreObserverRef.current.observe(node);
    },
    [fetchMore]
  );
  // ref コールバックは要素が消えるときに null で呼ばれるが、アンマウント経路では呼ばれないことがあるため明示的に切る
  React.useEffect(() => {
    return () => {
      loadMoreObserverRef.current?.disconnect();
    };
  }, []);

  const displayedAgents = agents;

  const loadMoreViewState = resolveLoadMoreViewState({
    hasMore,
    isLoadingMore,
    loadMoreError,
    isAutoLoadSupported,
    lastLoadAddedNothing,
  });
  const viewState = resolveAgentListViewState({ isLoading, error, agentCount: displayedAgents.length });

  const handleSearch = (tagIds: number[]) => {
    const query = new URLSearchParams(location.search);
    if (tagIds.length > 0) query.set("tags", tagIds.join(","));
    else query.delete("tags");
    if (isFromLearningArticle) query.set("source", AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE);
    const queryString = query.toString();
    history.replace({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : "",
    });
  };

  // 条件を解除して絞り込み前の一覧（相性スコア順）に戻す
  const handleResetConditions = () => {
    const query = new URLSearchParams(location.search);
    query.delete("tags");
    if (isFromLearningArticle) query.set("source", AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE);
    const queryString = query.toString();
    history.replace({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : "",
    });
  };

  // 遷移・送信元の判定はカード単位ではなく画面単位で決まる。
  const hasSearched = tagIdsFromUrl.length > 0;
  const detailSource = isFromLearningArticle
    ? AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE
    : originSource === AGENT_DETAIL_URL_SOURCE.TOP
      ? AGENT_DETAIL_URL_SOURCE.TOP
      : hasSearched
        ? AGENT_DETAIL_URL_SOURCE.SEARCH_RESULT
        : (originSource ?? AGENT_DETAIL_URL_SOURCE.AGENT_SEARCH);
  // 一覧の並び順（上位3人に王冠）を詳細にも引き継ぐ。rank は一覧全体での順位（1 始まり）
  const openDetail = (agent: Agent, rank: number) => {
    if (!hasUsableAgentId(agent)) return;
    history.push(`/agent_search/${agent.id}?source=${detailSource}`, {
      searchReturnTo: `${location.pathname}${location.search}`,
      compatibilityRank: rank,
    });
  };
  // 条件で絞り込んだ一覧は従来のカード（Figma「施策_条件で絞り込んだ一覧」）
  const renderAgentCard = (agent: Agent, index: number) => {
    const agentId = hasUsableAgentId(agent) ? agent.id : undefined;
    return (
      <CompatibilityAgentCard
        key={agentId ?? `no-id-${index}`}
        agent={agent}
        variant="search"
        rank={index + 1}
        showRadar={false}
        showBadges
        ctaLabel="詳細へ"
        onClickCard={() => openDetail(agent, index + 1)}
        onClickCta={() => openDetail(agent, index + 1)}
      />
    );
  };
  // 条件なしの一覧は登録導線（診断結果）と同じ見せ方: 上位3人を大判カード、4人目以降を一覧カード。
  // 追加読み込みで増えた分も一覧カードの末尾に足されていく
  const featuredAgents = displayedAgents.slice(0, FEATURED_PLANNER_COUNT);
  const otherAgents = displayedAgents.slice(FEATURED_PLANNER_COUNT);
  const renderFeaturedCard = (agent: Agent, index: number) => (
    <FeaturedPlannerCard
      key={agent.id ?? `featured-${index}`}
      agent={agent}
      rank={index + 1}
      onDetail={() => openDetail(agent, index + 1)}
    />
  );
  const recommendedList = (
    <>
      {isMobile ? (
        <Box className={classes.carouselBleed}>
          <RecommendedPlannerCarousel
            agents={featuredAgents}
            reduced={reduced}
            onDetail={(agent) => openDetail(agent, featuredAgents.indexOf(agent) + 1)}
          />
        </Box>
      ) : (
        <Box className={classes.featuredGrid}>{featuredAgents.map(renderFeaturedCard)}</Box>
      )}
      {otherAgents.length > 0 && (
        <Box component="section" className={classes.otherPlanners} aria-labelledby="other-planners-heading">
          <Box>
            <Typography component="h3" id="other-planners-heading" className={classes.otherHeading}>
              {OTHER_PLANNERS_HEADING}
            </Typography>
            <Typography className={classes.otherDescription}>{OTHER_PLANNERS_DESCRIPTION}</Typography>
          </Box>
          <Box className={classes.list}>
            {otherAgents.map((agent, index) => {
              const rank = FEATURED_PLANNER_COUNT + index + 1;
              return (
                <PlannerListCard
                  key={agent.id ?? `other-${index}`}
                  agent={agent}
                  rank={rank}
                  onDetail={() => openDetail(agent, rank)}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
  const searchProps = {
    onClose: () => setModalOpen(false),
    onSearch: handleSearch,
    selectedConditions: tagIdsFromUrl,
    tags: masterTags,
    isTagsLoading: isMasterTagsLoading,
    onRetryTags: () => {
      void refetchMasterTags();
    },
    onReset: hasConditions ? handleResetConditions : undefined,
  };
  const result = (
    <Box className={classes.results}>
      <Box className={classes.heading}>
        <Box className={classes.titleRow}>
          <Typography component="h2" className={classes.resultTitle}>
            あなたにおすすめの<span className={classes.plannerLabel}>認定プランナー</span>
          </Typography>
          {!isMobile && typeof total === "number" && <Typography className={classes.count}>{total}名</Typography>}
        </Box>
        <Typography className={classes.description}>
          {hasConditions ? RESULT_DESCRIPTION_WITH_CONDITIONS : RESULT_DESCRIPTION_WITHOUT_CONDITIONS}
        </Typography>
      </Box>
      {viewState === "loading" &&
        (isMobile ? (
          <Box className={classes.state}>
            <CircularProgress size={40} />
            <Typography className={classes.description}>プランナーを探しています</Typography>
          </Box>
        ) : (
          <Box className={classes.list}>
            {[1, 2, 3, 4].map((id) => (
              <Box key={id} className={classes.skeleton}>
                <Box className={classes.skeletonProfile}>
                  <Box className={classes.skeletonAvatar} />
                  <Box className={classes.skeletonHead}>
                    <Box className={classes.skeletonLine} width="70%" />
                    <Box className={classes.skeletonLine} width="50%" />
                  </Box>
                </Box>
                <Box className={classes.skeletonLine} />
                <Box className={classes.skeletonLine} />
              </Box>
            ))}
          </Box>
        ))}
      {viewState === "error" && (
        <Box className={classes.state}>
          <Typography component="h3" className={classes.stateTitle}>
            プランナーを読み込めませんでした
          </Typography>
          <Typography className={classes.description}>通信状況を確認して、もう一度お試しください。</Typography>
          <CustomButton tone="outline" buttonSize="M" onClick={() => fetchAgents(tagIdsFromUrl)}>
            再読み込み
          </CustomButton>
        </Box>
      )}
      {viewState === "empty" && (
        <Box className={classes.state}>
          <Typography component="h3" className={classes.stateTitle}>
            {hasConditions ? EMPTY_MESSAGE_WITH_CONDITIONS : EMPTY_MESSAGE_WITHOUT_CONDITIONS}
          </Typography>
          {hasConditions && (
            <Typography className={classes.description}>条件を変更するか、リセットしてお試しください。</Typography>
          )}
          <CustomButton
            tone="outline"
            buttonSize="M"
            onClick={hasConditions ? handleResetConditions : () => fetchAgents(tagIdsFromUrl)}
          >
            {hasConditions ? "条件をリセットする" : "再読み込みする"}
          </CustomButton>
        </Box>
      )}
      {viewState === "list" && (
        <>
          {hasConditions ? <Box className={classes.list}>{displayedAgents.map(renderAgentCard)}</Box> : recommendedList}
          {loadMoreViewState === "sentinel" && (
            <div data-testid="load-more-sentinel" ref={loadMoreSentinelRef} style={{ height: 1 }} />
          )}
          {loadMoreViewState === "loading" && (
            <Box className={classes.more}>
              <CircularProgress size={40} />
            </Box>
          )}
          {(loadMoreViewState === "manual" || loadMoreViewState === "error") && (
            <Box className={classes.more}>
              {loadMoreViewState === "error" && (
                <Typography className={classes.description}>{LOAD_MORE_ERROR_MESSAGE}</Typography>
              )}
              <CustomButton tone="outline" buttonSize="M" onClick={() => void fetchMore()}>
                {loadMoreViewState === "error" ? "再試行する" : "続きを読み込む"}
              </CustomButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
  return (
    <BaseTemplate
      headerTitle={isMobile ? "認定プランナーを探す" : undefined}
      navigationPath={NavigationPath.AGENT_SEARCH}
      isPcV2
      isDense
    >
      <Box className={classes.content}>
        {isMobile ? (
          <>
            <SearchModal {...searchProps} open={isModalOpen} />
            <Box className={classes.conditionBar}>
              {conditionLabel && <Typography className={classes.conditionLabel}>検索条件：{conditionLabel}</Typography>}
              <Box className={classes.conditionActions}>
                {hasConditions && (
                  <CustomButton tone="text" buttonSize="M" onClick={handleResetConditions}>
                    条件をリセットする
                  </CustomButton>
                )}
                <CustomButton tone="outline" buttonSize="M" fullWidth onClick={() => setModalOpen(true)}>
                  条件で詳細検索
                </CustomButton>
              </Box>
            </Box>
            {result}
          </>
        ) : (
          <>
            <Typography component="h1" className={classes.pageTitle}>
              認定プランナーを探す
            </Typography>
            <Box className={classes.columns}>
              <Box className={classes.side}>
                <SearchModal {...searchProps} open inline />
              </Box>
              {result}
            </Box>
          </>
        )}
      </Box>
    </BaseTemplate>
  );
};
